import React, { useContext } from 'react';
import classes from '@/experiment-management-shared/components/AddBuiltInVisualizationModal/subcomponents/LibaryPanelTabs/LibraryPanelOptions/LibraryPanelOptions.module.scss';
import { ActionMenuList, List, ListItem, Tooltip } from '@ds';
import { useLocation } from 'react-router';
import { format } from 'date-fns';
import { ActionMenuListContext } from '@ds/lists/List/ActionMenuList/useActionMenuListContext';
import usePanelTemplateRevisions from '@/experiment-management-shared/api/library-panel/usePanelTemplateRevisions';
import {
  RevisionDetail,
  PanelTemplate
} from '@/experiment-management-shared/api/library-panel/types';
import { BASE_API_URL } from '@/constants/configConstants';
import { getURLQueryParams } from '@shared/utils/url';
import { useHistory } from 'react-router-dom';
import { SELECT_PANEL_PATH } from '@/experiment-management-shared/constants/visualizationConstants';
import useIsUserLoggedIn from '@shared/hooks/useIsUserLoggedIn';
import { useSelector } from 'react-redux';
import { isUserAllowedToEditProject } from '@/reducers/projectsReducer';

type LibraryPanelOptionsProps = {
  templateId: string;
  selectedRevisionId: string | null;
  onRestartSession: () => void;
  template?: PanelTemplate;
};

const LibraryPanelOptions = ({
  templateId,
  selectedRevisionId,
  onRestartSession,
  template
}: LibraryPanelOptionsProps) => {
  const location = useLocation();

  const context = useContext(ActionMenuListContext);
  const queryParams = getURLQueryParams(location.hash);
  const isPreview = queryParams.get('isPreview') === 'true';
  const isUserLoggedIn = useIsUserLoggedIn();
  const canEdit = useSelector(isUserAllowedToEditProject);

  const history = useHistory();
  const { data: revisions } = usePanelTemplateRevisions(templateId, {
    refetchOnMount: true,
    enabled: !!templateId
  });

  const updatePanelRevisionId = (revision: RevisionDetail) => {
    const hashParams = new URLSearchParams(
      history.location.hash.replace(`#${SELECT_PANEL_PATH}?`, '')
    );

    hashParams.set('revisionId', revision.revision.toString());
    history.push({
      ...history.location,
      hash: `#${SELECT_PANEL_PATH}?${hashParams.toString()}`
    });

    context?.closePopover();
  };

  const renderRevisionItem = (revisionDetail: RevisionDetail) => {
    return (
      <ListItem
        active={
          selectedRevisionId
            ? revisionDetail.revision.toString() === selectedRevisionId
            : revisionDetail.revision === revisions?.[0].revision
        }
        onClick={() => updatePanelRevisionId(revisionDetail)}
        key={revisionDetail.revision}
        dataTest="version-history-item"
      >
        <ListItem.Text
          primary={format(
            new Date(revisionDetail.revision),
            'MM/dd/yy hh:mm a'
          )}
          secondary={revisionDetail.createdBy}
        />
      </ListItem>
    );
  };

  return (
    <ActionMenuList.Body
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ style: { marginTop: 4, marginLeft: 6 } }}
      closeMenuOnSelect
    >
      <Tooltip
        content="Verion history is only available in edit mode"
        placement="right"
        disableListeners={!isPreview}
      >
        <ListItem disabled={!templateId || isPreview}>
          <ListItem.Text primary="Version history" />
          <ListItem.Submenu offset="-6px 2px 0 0">
            <List>
              <div className={classes.versionHistoryContainer}>
                {revisions?.map(renderRevisionItem)}
              </div>
            </List>
          </ListItem.Submenu>
        </ListItem>
      </Tooltip>

      <ListItem
        onClick={() => {
          window.location.href = `${BASE_API_URL}code-visualizations/template/${templateId}/download`;
        }}
        disabled={!templateId}
      >
        <ListItem.Text primary="Export" />
      </ListItem>

      {isUserLoggedIn && canEdit && template?.code?.type !== 'js' && (
        <ListItem onClick={onRestartSession} dataTest="restart-session-btn">
          <ListItem.Text primary="Restart session" />
        </ListItem>
      )}
    </ActionMenuList.Body>
  );
};

export default React.memo(LibraryPanelOptions);
